import { FCWithChildren } from '@types';
import React from 'react';
import useFitText from './use_fit_text';
import { useHistory } from 'react-router-dom';
import classNames from 'classnames';
import { ActionButtonIcon, ActionSection, Button, Loading } from '@components';
import useLayoutContext from '@hooks/useLayoutContext';
import useHandleRedirect from '@hooks/useHandleRedirect';
import { MdAccountCircle, MdAdd, MdDevices } from 'react-icons/md';

type WelcomeOptionsProps = {
  isSigningIn: boolean;
  welcome: string;
  toggleFlag: () => void;
};

const WelcomeOptions: FCWithChildren<WelcomeOptionsProps> = ({
  isSigningIn,
  welcome,
  toggleFlag,
}) => {
  const history = useHistory();
  const {
    isCaptivePortal,
    portalAccessUrl,
    isGuestOnly,
    isSso,
    authType,
    schoolUidName,
    redirectToLogin,
    showGuestLoginButton,
    userVerificationOnly,
    isCampusDataReady,
  } = useLayoutContext();

  useHandleRedirect();

  const register = () => {
    history.push(portalAccessUrl('/register'));
  };
  const guestVisitors = () => {
    history.push(portalAccessUrl('/guest-visitors'));
  };
  const verificationRegister = () => {
    history.push(portalAccessUrl('/verification/register'));
  };

  const containerCls = classNames(
    isCaptivePortal
      ? 'absolute top-0 left-0 right-0 bg-white p-3 flex justify-center'
      : ['flex flex-col items-center max-w-[420px]', isSigningIn ? 'rounded-l-md' : 'md:shadow-xl'],
  );

  // Until the campus lookup resolves, `isSso`/`isGuestOnly`/`casLoginUrl` are all
  // still at their defaults, so we would render the wrong set of buttons and
  // `redirectToLogin` would fall through to the captive-portal login route
  // instead of the school's SSO portal. Hold the actions back until it lands.
  const showCreateAccount = isCampusDataReady && !(isSso || userVerificationOnly || isGuestOnly);
  const showSignIn = isCampusDataReady && !(isSigningIn || userVerificationOnly || isGuestOnly);
  const hasTwoActionButtons = showCreateAccount && showSignIn;

  const boxHeight = () => {
    const windowInnerWidth = window.innerWidth;
    if (windowInnerWidth >= 1280) {
      return 90;
    } else if (windowInnerWidth >= 1024) {
      return 75;
    }
    return 60;
  };

  const largeSizedFont = () => {
    const windowInnerWidth = window.innerWidth;
    if (windowInnerWidth > 1280) {
      return 187;
    }
    return 125;
  };

  const { fontSize, ref } = useFitText({ maxFontSize: largeSizedFont() });

  return (
    <>
      <ActionSection className={containerCls}>
        <ActionSection.Header>
          <h1 ref={ref} className="text-[20px] font-medium">
            {welcome}
          </h1>
        </ActionSection.Header>
        <ActionSection.Body className="pt-5">
          <div
            className={classNames(
              'flex flex-row gap-4 mb-4 justify-center',
              hasTwoActionButtons && 'py-2',
            )}
            id="welcome-options-buttons"
          >
            {!isCampusDataReady && (
              <div
                className="flex items-center justify-center py-8"
                role="status"
                aria-label="Loading sign-in options"
              >
                <Loading />
              </div>
            )}

            {showCreateAccount && (
              <Button
                layout="actionPrimary"
                className={`font-black ${isSigningIn ? 'mt-8' : ''}`}
                onClick={register}
                icon={() => <ActionButtonIcon icon={MdAccountCircle} badgeIcon={MdAdd} />}
              >
                Create Account
              </Button>
            )}

            {showGuestLoginButton && !userVerificationOnly && isGuestOnly && (
              <Button
                layout="actionPrimary"
                className={`font-black ${isSigningIn ? 'mt-8' : ''}`}
                onClick={guestVisitors}
                icon={() => <ActionButtonIcon icon={MdAccountCircle} badgeIcon={MdAdd} />}
              >
                Guests and Visitors
              </Button>
            )}

            {userVerificationOnly && (
              <Button
                className="font-black"
                layout="actionPrimary"
                onClick={verificationRegister}
                icon={() => <ActionButtonIcon icon={MdDevices} />}
              >
                Register Device
              </Button>
            )}

            {showSignIn && (
              <Button
                layout="actionSecondary"
                icon={MdAccountCircle}
                onClick={() =>
                  redirectToLogin(() => {
                    toggleFlag();
                  })
                }
              >
                {authType === 'sso' && schoolUidName !== ''
                  ? `Sign In ( ${schoolUidName} )`
                  : `Sign In`}
              </Button>
            )}
          </div>
          {isCampusDataReady && !(userVerificationOnly || isGuestOnly) && (
            <ActionSection.Link
              onClick={() => {
                isSso
                  ? history.push('/support')
                  : !isSso && !isSigningIn
                  ? history.push(portalAccessUrl('/forgot-password'))
                  : history.push('/');
              }}
            >
              {isSso ? 'Need help with sign-in?' : 'Forgot Username or Password?'}
            </ActionSection.Link>
          )}

          {showGuestLoginButton && !userVerificationOnly && !isGuestOnly && (
            <ActionSection.Link onClick={guestVisitors}>Guests and Visitors</ActionSection.Link>
          )}
        </ActionSection.Body>
      </ActionSection>
    </>
  );
};

export default WelcomeOptions;
